import React from 'react'
import { Container, ContainerProps, Theme, useMediaQuery } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import NavigationGroupLink from './NavigationGroupLink'
import NavigationGroupAccordion from './NavigationGroupAccordion'
import {
  ExternalLink,
  NavigationGroup as NavigationGroupType,
  NavTitles,
} from '../../types/page'
import useUser from '../../hooks/useUser'
import AccountDropdownDesktop from './AccordionDropdown/AccountDropdownDesktop'
import AccountDropdownMobile from './AccordionDropdown/AccountDropdownMobile'
import usePageData from '../../hooks/usePageData'
import { BrandNames } from '../../types/brand'
import NavigationGroupAccordionDesktop from './NavigationGroupAccordionDesktop'

export type NavigationGroupProps = Omit<ContainerProps, 'children'> & {
  navigationGroup: NavigationGroupType
  accordionClass?: string
  accordionClasses?: {
    root?: string
    expandIconWrapper?: string
    accordionSummary?: string
    accordionSummaryContent?: string
    accordionSummaryExpanded?: string
    accordionDetails?: string
    accordionDetailEntry?: string
    accEntryLink?: string
  }
  groupClass?: string
  linkClass?: string
  expanded?: string | false
  handleChange?: (
    s: string
  ) => (event: React.ChangeEvent<unknown>, newExpanded: boolean) => void
  handleHoverChange?: (s: string) => void
}

const NavigationGroup: React.FC<NavigationGroupProps> = ({
  navigationGroup,
  accordionClass,
  accordionClasses,
  groupClass,
  linkClass,
  expanded,
  handleChange,
  handleHoverChange,
  ...props
}) => {
  const { classes, cx } = useStyles()
  const { lead_auth_token: leadAuthToken } = useUser()
  const { brand } = usePageData()
  const isDesktop = useMediaQuery<Theme>((theme) => theme.breakpoints.up('lg'))

  const externalLinkNav = navigationGroup as NavigationGroupType<ExternalLink>

  if (navigationGroup.variant === 'Dropdown') {
    if (brand?.name.toLowerCase() === BrandNames.zipJob) {
      return (
        <NavigationGroupAccordion
          // @TODO: Remove `accordionClass` prop. Make the respective changes in ZipJob.
          accordionClass={accordionClass}
          accordionClasses={accordionClasses}
          navGroup={navigationGroup}
          expanded={expanded}
          handleChange={handleChange}
        />
      )
    }

    return isDesktop ? (
      <NavigationGroupAccordionDesktop
        accordionClass={accordionClass}
        accordionClasses={accordionClasses}
        navGroup={navigationGroup}
        expanded={expanded}
        handleChange={handleHoverChange}
      />
    ) : (
      <NavigationGroupAccordion
        accordionClass={accordionClass}
        accordionClasses={accordionClasses}
        navGroup={navigationGroup}
        expanded={expanded}
        handleChange={handleChange}
      />
    )
  }

  if (
    navigationGroup.title === NavTitles.Account &&
    leadAuthToken &&
    brand?.name.toLowerCase() !== BrandNames.zipJob
  ) {
    return isDesktop ? (
      <AccountDropdownDesktop
        accordionClass={accordionClass}
        accordionClasses={accordionClasses}
        navGroup={externalLinkNav}
        expanded={expanded}
        handleChange={handleHoverChange}
      />
    ) : (
      <AccountDropdownMobile
        accordionClass={accordionClass}
        accordionClasses={accordionClasses}
        navGroup={externalLinkNav}
        expanded={expanded}
        handleChange={handleChange}
      />
    )
  }

  return (
    <Container
      className={cx(classes.container, groupClass)}
      data-contentful-id={navigationGroup.contentful_id}
      disableGutters
      key={navigationGroup.contentful_id}
      {...props}
    >
      {navigationGroup.links.map((link) => (
        <NavigationGroupLink
          key={link.contentful_id}
          link={link}
          className={linkClass}
        />
      ))}
    </Container>
  )
}

const useStyles = makeStyles()({
  container: {
    display: 'flex',
    margin: 'unset',
    width: '75%',
  },
})

export default NavigationGroup
